// Flat-to-tree sidebar renderer. Groups paths by `/` into collapsible
// folders. Clicking a leaf dispatches an OPEN_EVENT on the container
// with the path and kind, so main.ts can open code in a tab and data in
// the dataset viewer.

import type { FileEntry, FolderSource } from './fs';
import { formatSize, type FileKind } from './data-kinds';
import { icon } from './icons';

// Shared with main.ts's listener — a bare string literal on each side let the
// two drift apart once already (dispatcher said 'sassy:open', listener
// 'kolistat:open', and tree clicks silently died).
export const OPEN_EVENT = 'kolilang:open';

export interface OpenEventDetail {
  path: string;
  kind: FileKind;
}

export interface FileTreeOptions {
  /// When given, data entries show a dot: filled when the file is
  /// loaded into the DuckDB session, hollow when not. Cloud sources
  /// pass this; local sources omit it because their files are always
  /// available.
  loaded?: (path: string) => boolean;
}

export const TREE_EMPTY_TEXT =
  "no .sas or data files here. Data files can be read with proc import datafile='name.csv' dbms=csv.";

interface TreeNode {
  name: string;
  entry: FileEntry | null; // the file at this path, if any; a folder node can carry one too
  children: Map<string, TreeNode>;
}

function buildTree(entries: FileEntry[]): TreeNode {
  const root: TreeNode = { name: '', entry: null, children: new Map() };
  for (const e of entries) {
    const parts = e.path.split('/');
    let cur = root;
    for (let i = 0; i < parts.length; i += 1) {
      const part = parts[i];
      let next = cur.children.get(part);
      if (!next) {
        next = { name: part, entry: null, children: new Map() };
        cur.children.set(part, next);
      }
      cur = next;
    }
    cur.entry = e;
  }
  return root;
}

export function renderFileTree(
  container: HTMLElement,
  source: FolderSource,
  entries: FileEntry[],
  activePath: string | null = null,
  opts: FileTreeOptions = {},
): void {
  container.innerHTML = '';
  const header = document.createElement('div');
  header.className = 'tree-header';
  header.textContent = `${source.label} · on this device`;
  header.title = `${entries.length} files`;
  container.appendChild(header);

  if (entries.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'tree-empty';
    empty.textContent = TREE_EMPTY_TEXT;
    container.appendChild(empty);
    return;
  }

  const tree = buildTree(entries);
  const ul = document.createElement('ul');
  ul.className = 'tree-root';
  renderNode(container, tree, ul, 0, activePath, opts);
  container.appendChild(ul);
}

function renderNode(
  root: HTMLElement,
  node: TreeNode,
  parent: HTMLElement,
  depth: number,
  activePath: string | null,
  opts: FileTreeOptions,
): void {
  // children sorted: folders first (have sub-children), then files.
  const entries = [...node.children.values()].sort((a, b) => {
    const aIsLeaf = a.children.size === 0;
    const bIsLeaf = b.children.size === 0;
    if (aIsLeaf !== bIsLeaf) return aIsLeaf ? 1 : -1;
    return a.name.localeCompare(b.name);
  });

  for (const child of entries) {
    const isLeaf = child.children.size === 0;
    const li = document.createElement('li');
    li.style.paddingLeft = `${depth * 12 + 8}px`;

    const chev = document.createElement('span');
    chev.className = 'chev';
    chev.textContent = isLeaf ? ' ' : '⌄';
    li.appendChild(chev);

    if (isLeaf && child.entry) {
      renderLeaf(root, li, child.entry, child.name, activePath, opts);
      parent.appendChild(li);
      continue;
    }

    li.className = 'tree-dir';
    const label = document.createElement('span');
    label.className = 'tree-label';
    label.textContent = child.name;
    li.appendChild(label);
    const subUl = document.createElement('ul');
    subUl.className = 'tree-subtree';
    subUl.style.display = 'block';
    li.addEventListener('click', (ev) => {
      const shown = subUl.style.display !== 'none';
      subUl.style.display = shown ? 'none' : 'block';
      chev.textContent = shown ? '❯' : '⌄';
      ev.stopPropagation();
    });
    parent.appendChild(li);
    parent.appendChild(subUl);
    // A path that is both a file and a folder prefix, which a flat key
    // store can produce, keeps its own row at the top of its subtree.
    if (child.entry) {
      const own = document.createElement('li');
      own.style.paddingLeft = `${(depth + 1) * 12 + 8}px`;
      const ownChev = document.createElement('span');
      ownChev.className = 'chev';
      ownChev.textContent = ' ';
      own.appendChild(ownChev);
      renderLeaf(root, own, child.entry, child.name, activePath, opts);
      subUl.appendChild(own);
    }
    renderNode(root, child, subUl, depth + 1, activePath, opts);
  }
}

function renderLeaf(
  root: HTMLElement,
  li: HTMLLIElement,
  entry: FileEntry,
  name: string,
  activePath: string | null,
  opts: FileTreeOptions,
): void {
  const isData = entry.kind === 'data';
  li.className = isData ? 'tree-file tree-data' : 'tree-file tree-code';
  if (entry.path === activePath) li.classList.add('active');

  if (isData) {
    const glyph = icon('grid');
    glyph.classList.add('tree-glyph');
    li.appendChild(glyph);
  }

  const label = document.createElement('span');
  label.className = 'tree-label';
  label.textContent = name;
  li.appendChild(label);

  if (isData) {
    const size = document.createElement('span');
    size.className = 'tree-size';
    size.textContent = formatSize(entry.size);
    li.appendChild(size);
    if (opts.loaded) {
      const loaded = opts.loaded(entry.path);
      const dot = document.createElement('span');
      dot.className = loaded ? 'tree-dot loaded' : 'tree-dot';
      dot.title = loaded ? 'loaded into this session' : 'not loaded yet';
      li.appendChild(dot);
    }
    li.title = `open ${entry.path} in the dataset viewer`;
  }

  const detail: OpenEventDetail = { path: entry.path, kind: entry.kind };
  li.addEventListener('click', () => {
    root.dispatchEvent(new CustomEvent<OpenEventDetail>(OPEN_EVENT, { detail, bubbles: true }));
  });
}
